import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { container } from "@/container";
import { TYPES } from "@/types/symbols";
import { IPermissionService } from "@/interfaces/services";
import {
  ApplicationShare,
  ApplicationShareDeleteQuery,
  ApplicationShareInput,
  Permission,
  PermissionScope,
} from "@/types/permission";
import { WorkflowPermissionOptions } from "@/types/domain";
import {
  BackendFormPermissionDeleteQuery,
  BackendWorkflowPermissionDeleteQuery,
} from "@/schemas/permission/response";

const usePermissionService = () =>
  container.get<IPermissionService>(TYPES.PermissionService);

type WorkflowPermissionTarget = {
  workflowId: string;
  major?: number;
};

const workflowPermissionTargetKey = (workflowId?: string, major?: number) =>
  `${workflowId ?? ""}:${major ?? "latest"}`;

const workflowPermissionsQueryKey = (
  workflowId?: string,
  options?: WorkflowPermissionOptions,
) => ["workflow-permissions", workflowId, options?.major ?? "latest"];
const formPermissionsQueryKey = (formId?: string) => [
  "form-permissions",
  formId,
];
const applicationSharesQueryKey = (serialNumber?: string) => [
  "application-shares",
  serialNumber,
];

export const useWorkflowPermissions = (
  workflowId?: string,
  options?: WorkflowPermissionOptions,
) => {
  const permissionService = usePermissionService();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: workflowPermissionsQueryKey(workflowId, options),
    queryFn: () =>
      permissionService.getWorkflowPermissions(workflowId as string, options),
    enabled: !!workflowId,
  });

  return {
    permission: data?.data ?? {
      scope: PermissionScope.ADMIN,
      permissions: { user: [], role: [], org: [] },
    },
    isLoading,
    isError,
    error,
    refetch,
  };
};
export const useWorkflowListPermissions = (
  targets: WorkflowPermissionTarget[] = [],
) => {
  const permissionService = usePermissionService();
  const queries = useQueries({
    queries: targets.map((target) => ({
      queryKey: workflowPermissionsQueryKey(target.workflowId, {
        major: target.major,
      }),
      queryFn: () =>
        permissionService.getWorkflowPermissions(target.workflowId, {
          major: target.major,
        }),
      enabled: !!target.workflowId,
    })),
  });

  const permissionsByWorkflowKey = useMemo(() => {
    return targets.reduce<Record<string, Permission>>((acc, target, index) => {
      const data = queries[index]?.data?.data ?? {
        scope: PermissionScope.ADMIN,
        permissions: { user: [], role: [], org: [] },
      };
      acc[workflowPermissionTargetKey(target.workflowId, target.major)] = data;
      return acc;
    }, {});
  }, [queries, targets]);

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);
  const errors = queries.map((query) => query.error).filter(Boolean);

  return {
    getWorkflowPermission: (workflowId: string, major?: number) =>
      permissionsByWorkflowKey[workflowPermissionTargetKey(workflowId, major)],
    permissionsByWorkflowKey,
    isLoading,
    isError,
    errors,
  };
};

export const useAddWorkflowPermissions = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      workflowId,
      major,
      permission,
    }: {
      workflowId: string;
      major?: number;
      permission: Permission;
    }) =>
      permissionService.addWorkflowPermissions(workflowId, permission, {
        major,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workflowPermissionsQueryKey(variables.workflowId, {
          major: variables.major,
        }),
      });
    },
  });
};

export const useUpdateWorkflowPermissions = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      workflowId,
      major,
      permission,
    }: {
      workflowId: string;
      major?: number;
      permission: Permission;
    }) =>
      permissionService.updateWorkflowPermissions(workflowId, permission, {
        major,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workflowPermissionsQueryKey(variables.workflowId, {
          major: variables.major,
        }),
      });
    },
  });
};

export const useDeleteWorkflowPermissions = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      workflowId,
      major,
      query,
    }: {
      workflowId: string;
      major?: number;
      query?: BackendWorkflowPermissionDeleteQuery;
    }) =>
      permissionService.deleteWorkflowPermissions(workflowId, query, {
        major,
      }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workflowPermissionsQueryKey(variables.workflowId, {
          major: variables.major,
        }),
      });
    },
  });
};

export const useDeleteWorkflowPermission = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({ id }: { workflowId: string; id: number }) =>
      permissionService.deleteWorkflowPermission(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: workflowPermissionsQueryKey(variables.workflowId),
      });
    },
  });
};

export const useFormPermissions = (formId?: string) => {
  const permissionService = usePermissionService();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: formPermissionsQueryKey(formId),
    queryFn: () => permissionService.getFormPermissions(formId as string),
    enabled: !!formId,
  });

  return {
    permissions: data?.data ?? {
      scope: PermissionScope.ADMIN,
      permissions: { user: [], role: [], org: [] },
    },
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useAddFormPermissions = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      formId,
      permission,
    }: {
      formId: string;
      permission: Permission;
    }) => permissionService.addFormPermissions(formId, permission),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: formPermissionsQueryKey(variables.formId),
      });
    },
  });
};

export const useUpdateFormPermissions = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      formId,
      permission,
    }: {
      formId: string;
      permission: Permission;
    }) => permissionService.updateFormPermissions(formId, permission),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: formPermissionsQueryKey(variables.formId),
      });
    },
  });
};

export const useDeleteFormPermissions = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      formId,
      query,
    }: {
      formId: string;
      query?: BackendFormPermissionDeleteQuery;
    }) => permissionService.deleteFormPermissions(formId, query),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: formPermissionsQueryKey(variables.formId),
      });
    },
  });
};

export const useDeleteFormPermission = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({ formId, id }: { formId: string; id: number }) =>
      permissionService.deleteFormPermission(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: formPermissionsQueryKey(variables.formId),
      });
    },
  });
};

export const useApplicationShares = (serialNumber?: string) => {
  const permissionService = usePermissionService();
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: applicationSharesQueryKey(serialNumber),
    queryFn: () =>
      permissionService.getApplicationShares(serialNumber as string),
    enabled: !!serialNumber,
  });

  return {
    shares: data?.data ?? ([] as ApplicationShare[]),
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useAddApplicationShares = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      serialNumber,
      shares,
    }: {
      serialNumber: string;
      shares: ApplicationShareInput[];
    }) => permissionService.addApplicationShares(serialNumber, shares),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicationSharesQueryKey(variables.serialNumber),
      });
    },
  });
};

export const useUpdateApplicationShares = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      serialNumber,
      shares,
    }: {
      serialNumber: string;
      shares: ApplicationShareInput[];
    }) => permissionService.updateApplicationShares(serialNumber, shares),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicationSharesQueryKey(variables.serialNumber),
      });
    },
  });
};

export const useDeleteApplicationShares = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({
      serialNumber,
      query,
    }: {
      serialNumber: string;
      query: ApplicationShareDeleteQuery;
    }) => permissionService.deleteApplicationShares(serialNumber, query),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicationSharesQueryKey(variables.serialNumber),
      });
    },
  });
};

export const useDeleteApplicationShare = () => {
  const queryClient = useQueryClient();
  const permissionService = usePermissionService();

  return useMutation({
    mutationFn: ({ serialNumber, id }: { serialNumber: string; id: number }) =>
      permissionService.deleteApplicationShare(id),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: applicationSharesQueryKey(variables.serialNumber),
      });
    },
  });
};
